import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorPanel } from "../components/EditorPanel";
import { SidePanel } from "../components/SidePanel";
import { Layout } from "../pages/Layout";
import { getShareByCode, type GetShareResponse } from "../services/share";
import { directionFromShare } from "../utils/direction";

export const Route = createFileRoute("/share/$shareCode")({
  component: SharePage,
});

function SharePage() {
  const { shareCode } = Route.useParams();
  const { t } = useTranslation();
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [selectedDirection, setSelectedDirection] =
    useState<string>("lattocyr");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchShare() {
      setIsLoading(true);
      setError(null);

      const result: GetShareResponse = await getShareByCode(shareCode);

      if (!result.success || !result.data) {
        setError(result.message || t("share.notFound"));
        setIsLoading(false);
        return;
      }

      setInputText(result.data.original_text);
      setOutputText(result.data.translated_text);
      setSelectedDirection(
        directionFromShare(
          result.data.from_language,
          result.data.to_language,
        ),
      );
      setIsLoading(false);
    }

    fetchShare();
  }, [shareCode, t]);

  if (isLoading) {
    return (
      <Layout title={t("share.viewTitle")}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center text-gray-600 text-lg">
            {t("share.loading")}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={t("share.viewTitle")}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center text-red-600 text-lg">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t("share.viewTitle")}>
      <div className="editors-container flex-1 flex lg:min-h-0 bg-red-200 flex-col lg:flex-row">
        <EditorPanel
          readOnly
          inputText={inputText}
          setInputText={setInputText}
          outputText={outputText}
          setOutputText={setOutputText}
          handleTranslit={() => {}}
          selectedDirection={selectedDirection}
          onFromLanguageChange={() => {}}
          onToLanguageChange={() => {}}
          onDirectionSwap={() => {}}
        />
        <SidePanel
          readOnly
          selectedDirection={selectedDirection}
          setSelectedDirection={setSelectedDirection}
          isLoading={false}
          handleTranslit={() => {}}
          isAutoDetected={false}
          setInputText={setInputText}
          setOutputText={setOutputText}
          outputText={outputText}
        />
      </div>
    </Layout>
  );
}
